import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Shield, Mail, MapPin } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_industrial-shields/artifacts/otud2dwa_Flexi.png";

const LAST_UPDATE = "Junio 2026";

const sections = [
  {
    id: "responsable",
    title: "1. Identidad del responsable",
    body: (
      <>
        <p>
          El responsable del tratamiento de sus datos personales es{" "}
          <strong>Guarda Flex™</strong>, empresa dedicada a la fabricación,
          reparación y mantenimiento de sistemas de protección industrial y
          fuelles para maquinaria CNC, con domicilio y operaciones en{" "}
          <strong>Querétaro, Querétaro, México</strong> (zona de servicio:
          Querétaro, México).
        </p>
        <p>
          Para cualquier asunto relacionado con la protección de sus datos
          personales, puede contactarnos en el correo electrónico{" "}
          <a
            href="mailto:contacto@guardaflex.com.mx"
            className="text-[#00B4D8] hover:underline"
          >
            contacto@guardaflex.com.mx
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "datos",
    title: "2. Datos personales que recabamos",
    body: (
      <>
        <p>
          Los datos personales que recabamos son los que usted proporciona
          voluntariamente al enviarnos el formulario de contacto de este
          sitio web:
        </p>
        <ul>
          <li><strong>Nombre completo</strong></li>
          <li><strong>Número telefónico</strong></li>
          <li><strong>Correo electrónico</strong></li>
          <li><strong>Nombre de la empresa</strong> (opcional)</li>
          <li><strong>Mensaje</strong> con la descripción de su solicitud</li>
        </ul>
        <p>
          No recabamos datos personales sensibles en términos del artículo 3,
          fracción VI de la LFPDPPP.
        </p>
      </>
    ),
  },
  {
    id: "finalidades",
    title: "3. Finalidad del tratamiento",
    body: (
      <>
        <p>
          Los datos personales que recabamos serán utilizados exclusivamente
          para las siguientes finalidades relacionadas con la relación
          comercial que usted inicia con nosotros:
        </p>
        <ul>
          <li>Responder solicitudes de cotización.</li>
          <li>Brindar atención técnica y comercial.</li>
          <li>
            Dar seguimiento a solicitudes relacionadas con sistemas de
            protección industrial y fuelles para maquinaria CNC (guardas
            telescópicas, fuelles industriales, roll up covers, guardas
            metálicas flexibles y sistemas modulares).
          </li>
          <li>Coordinar visitas técnicas, entregas o servicios post-venta.</li>
        </ul>
      </>
    ),
  },
  {
    id: "arco",
    title: "4. Derechos ARCO",
    body: (
      <>
        <p>
          Usted tiene derecho a <strong>Acceder</strong> a sus datos
          personales, <strong>Rectificarlos</strong> cuando sean inexactos,
          solicitar su <strong>Cancelación</strong> cuando considere que ya
          no son necesarios, u <strong>Oponerse</strong> a su tratamiento
          para determinados fines.
        </p>
        <p>
          Para ejercer cualquiera de estos derechos, envíe una solicitud al
          correo electrónico{" "}
          <a
            href="mailto:contacto@guardaflex.com.mx"
            className="text-[#00B4D8] hover:underline"
          >
            contacto@guardaflex.com.mx
          </a>
          {" "}indicando su nombre completo, el derecho que desea ejercer y
          una descripción clara de los datos personales de los que se trata.
        </p>
        <p>
          Daremos respuesta a su solicitud en un plazo no mayor a 20 días
          hábiles a partir de su recepción.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "5. Uso de cookies",
    body: (
      <p>
        El sitio web <strong>guardaflex.com.mx</strong> utiliza cookies y
        tecnologías similares con fines de <strong>analítica</strong> (para
        entender cómo se utiliza el sitio) y para el{" "}
        <strong>funcionamiento técnico</strong> básico de la página. Estas
        cookies no recaban por sí mismas datos que permitan identificarlo
        personalmente. Usted puede deshabilitarlas en cualquier momento desde
        la configuración de su navegador.
      </p>
    ),
  },
  {
    id: "modificaciones",
    title: "6. Notificación de cambios",
    body: (
      <p>
        Cualquier actualización o modificación al presente Aviso de
        Privacidad será publicada en esta misma página, indicando la fecha
        de la última actualización. Le recomendamos revisarlo periódicamente
        para estar al tanto de los cambios que pudieran afectarle.
      </p>
    ),
  },
];

export default function AvisoDePrivacidad() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Aviso de Privacidad · Guarda Flex";
    return () => {
      document.title = "Guarda Flex — Protección industrial para maquinaria CNC";
    };
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="bg-[#0A1B35] text-white border-b-2 border-[#00B4D8]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="flex items-center gap-3 group"
          >
            <div className="bg-white px-3 py-1.5 shadow-lg" style={{ borderRadius: "2px" }}>
              <img src={LOGO_URL} alt="Guarda Flex" className="h-9 w-auto" />
            </div>
            <div className="font-display font-black text-base tracking-tight leading-none hidden sm:inline">
              <span className="text-white">GUARDA</span>
              <span className="text-slate-400"> FLEX</span>
              <sup className="ml-0.5 text-[9px] text-slate-400">™</sup>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/80 hover:text-[#00B4D8] transition-colors"
          >
            <ArrowLeft size={14} /> Volver al sitio
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-[#00B4D8]" />
            <span className="gf-overline">Documento legal</span>
          </div>
          <h1 className="font-display font-black text-[#0A1B35] text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-6">
            Aviso de Privacidad
          </h1>
          <p className="text-slate-600 text-base lg:text-lg max-w-3xl leading-relaxed">
            En cumplimiento con la Ley Federal de Protección de Datos
            Personales en Posesión de los Particulares (LFPDPPP), Guarda
            Flex™ pone a su disposición el presente Aviso de Privacidad que
            regula la recolección y tratamiento de sus datos personales.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-accent uppercase tracking-wider">
              <Shield size={14} className="text-[#00B4D8]" /> LFPDPPP — México
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-accent uppercase tracking-wider">
              <Mail size={14} className="text-[#00B4D8]" /> contacto@guardaflex.com.mx
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-accent uppercase tracking-wider">
              <MapPin size={14} className="text-[#00B4D8]" /> Querétaro, México
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-accent uppercase tracking-wider">
              Última actualización: {LAST_UPDATE}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-6">
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#00B4D8] mb-3">
                Contenido
              </div>
              <ul className="space-y-2 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-slate-600 hover:text-[#0A1B35] hover:underline transition-colors block leading-snug"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-12 text-slate-700 leading-relaxed">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                data-testid={`privacy-section-${s.id}`}
                className="scroll-mt-24"
              >
                <h2 className="font-display font-black text-[#0A1B35] text-xl lg:text-2xl tracking-tight leading-tight mb-4 pb-3 border-b-2 border-[#00B4D8]/40">
                  {s.title}
                </h2>
                <div className="prose-content space-y-4">{s.body}</div>
              </section>
            ))}

            <div className="mt-12 p-6 bg-[#F8FAFC] border-l-4 border-[#00B4D8]" style={{ borderRadius: "2px" }}>
              <p className="text-sm text-slate-700 leading-relaxed">
                Al utilizar los servicios de Guarda Flex™ y proporcionar sus
                datos personales mediante el formulario de contacto,
                manifiesta que ha leído, entendido y aceptado los términos
                del presente Aviso de Privacidad.
              </p>
              <p className="text-xs text-slate-500 mt-4 font-mono-accent uppercase tracking-wider">
                Última actualización: {LAST_UPDATE}
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/"
                data-testid="privacy-cta-home"
                className="gf-btn-secondary"
              >
                <ArrowLeft size={14} /> Volver al sitio
              </Link>
              <a
                href="mailto:contacto@guardaflex.com.mx?subject=Solicitud%20Derechos%20ARCO"
                data-testid="privacy-cta-arco"
                className="gf-btn-primary"
              >
                <Mail size={14} /> Ejercer Derechos ARCO
              </a>
            </div>
          </article>
        </div>
      </div>

      <footer className="bg-[#061226] text-white/60 text-xs py-6">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Guarda Flex™ — Todos los derechos reservados.</span>
          <Link to="/" className="hover:text-[#00B4D8] transition-colors uppercase tracking-widest">
            Sitio principal
          </Link>
        </div>
      </footer>
    </div>
  );
}
